import type { DayBlock, HourlyEntry } from '../types';

// Astronomical twilight is when the sun is 18° below the horizon — the
// "truly dark" threshold for astrophotography. Exact time depends on
// latitude and date; 90 minutes after sunset / before sunrise is a tolerable
// approximation for mid-latitudes in spring/summer/fall. For deep winter
// or high latitudes, swap this for a proper solar position algorithm.
const TWILIGHT_OFFSET_MIN = 90;

export type DewRisk = 'low' | 'moderate' | 'high';
export type CloudVerdict = 'clear' | 'fair' | 'cloudy' | 'overcast';

export interface HourDew {
  time: string;
  spreadF: number | null;
  humidity: number | null;
  risk: DewRisk;
}

// Summary of cloud cover during the imaging window. Bands chosen for
// visual astronomy / astrophotography practice:
//   ≤ 20%: usable (clear or thin high cloud, transparent imaging)
//   21–50%: marginal (broken cloud, hunt-and-peck between openings)
//   51–80%: mostly cloudy (rare usable gaps)
//   > 80%: overcast (don't bother setting up)
export interface CloudForecast {
  avgPct: number | null;
  minPct: number | null;
  maxPct: number | null;
  clearHours: number;
  fairHours: number;
  cloudyHours: number;
  verdict: CloudVerdict;
}

export interface ImagingWindow {
  start: string;
  end: string;
  hours: number;
  moonDownTheWholeTime: boolean;
  dewRisk: DewRisk;
  minSpreadF: number | null;
  maxHumidity: number | null;
  // When dew is first *expected to form on gear* inside the window
  // (first hour crossing into moderate risk).
  dewOnsetAt: string | null;
  // Peak (worst) dew hour — smallest temp-dewpoint spread.
  peakDewHour: string | null;
  peakDewSpreadF: number | null;
  hourly: HourDew[];
  // Cloud cover summary during the window (Open-Meteo hourly total cloud).
  clouds: CloudForecast;
}

// Astrophotography optics cool by ~5°F below ambient via radiation loss to
// a clear sky. That means gear hits dewpoint *before* the air does. Dew-
// friendly thresholds (spread = tempF - dewpointF):
//   high:     spread < 3°F  OR  RH ≥ 95%  → heavy dew on optics imminent
//   moderate: spread < 6°F  OR  RH ≥ 85%  → dew likely on optics
//   low:      spread ≥ 6°F  AND RH < 85%  → equipment dry through the session
export function hourDewRisk(h: HourlyEntry): DewRisk {
  if (h.dewpointF == null) return 'moderate';
  const spread = h.tempF - h.dewpointF;
  const rh = h.humidity ?? 0;
  if (spread < 3 || rh >= 95) return 'high';
  if (spread < 6 || rh >= 85) return 'moderate';
  return 'low';
}

export function computeImagingWindow(
  day: DayBlock,
  hourly: HourlyEntry[],
): ImagingWindow | null {
  const sunset = new Date(day.sunset);
  const astroDarkStart = new Date(sunset.getTime() + TWILIGHT_OFFSET_MIN * 60_000);
  const astroDarkEnd = new Date(sunset.getTime() + 10 * 3600_000);

  let windowStart = astroDarkStart;
  let windowEnd = astroDarkEnd;
  let moonDownAll = true;

  const moonRise = day.moonRise ? new Date(day.moonRise) : null;
  const moonSet = day.moonSet ? new Date(day.moonSet) : null;
  const brightMoon = day.moonIllumPct > 25;

  if (brightMoon && moonRise && moonSet) {
    if (moonSet > astroDarkStart && moonSet < astroDarkEnd) {
      windowStart = moonSet;
      moonDownAll = false;
    } else if (moonRise > astroDarkStart && moonRise < astroDarkEnd) {
      windowEnd = moonRise;
      moonDownAll = false;
    }
  }

  if (windowEnd <= windowStart) return null;
  const hours = (windowEnd.getTime() - windowStart.getTime()) / 3600_000;

  const windowHourly = hourly.filter((h) => {
    const t = new Date(h.time);
    return t >= windowStart && t <= windowEnd;
  });

  const perHour: HourDew[] = windowHourly.map((h) => {
    const spread = h.dewpointF != null ? Math.round((h.tempF - h.dewpointF) * 10) / 10 : null;
    return {
      time: h.time,
      spreadF: spread,
      humidity: h.humidity,
      risk: hourDewRisk(h),
    };
  });

  let minSpread: number | null = null;
  let maxHumidity: number | null = null;
  let peakHour: HourDew | null = null;
  let onsetAt: string | null = null;
  for (const hd of perHour) {
    if (hd.spreadF != null && (minSpread == null || hd.spreadF < minSpread)) {
      minSpread = hd.spreadF;
      peakHour = hd;
    }
    if (hd.humidity != null && (maxHumidity == null || hd.humidity > maxHumidity)) {
      maxHumidity = hd.humidity;
    }
    if (onsetAt == null && hd.risk !== 'low') {
      onsetAt = hd.time;
    }
  }

  const worst: DewRisk = perHour.some((h) => h.risk === 'high')
    ? 'high'
    : perHour.some((h) => h.risk === 'moderate')
      ? 'moderate'
      : 'low';

  const clouds = summarizeClouds(windowHourly);

  return {
    start: windowStart.toISOString(),
    end: windowEnd.toISOString(),
    hours: Math.round(hours * 10) / 10,
    moonDownTheWholeTime: moonDownAll,
    dewRisk: worst,
    minSpreadF: minSpread,
    maxHumidity,
    dewOnsetAt: onsetAt,
    peakDewHour: peakHour?.time ?? null,
    peakDewSpreadF: peakHour?.spreadF ?? null,
    hourly: perHour,
    clouds,
  };
}

function summarizeClouds(hours: HourlyEntry[]): CloudForecast {
  const values = hours
    .map((h) => h.cloudCoverPct)
    .filter((v): v is number => v != null);
  if (values.length === 0) {
    return {
      avgPct: null,
      minPct: null,
      maxPct: null,
      clearHours: 0,
      fairHours: 0,
      cloudyHours: 0,
      verdict: 'fair',
    };
  }
  let sum = 0;
  let min = Infinity;
  let max = -Infinity;
  let clear = 0;
  let fair = 0;
  let cloudy = 0;
  for (const v of values) {
    sum += v;
    if (v < min) min = v;
    if (v > max) max = v;
    if (v <= 20) clear++;
    else if (v <= 50) fair++;
    else cloudy++;
  }
  const avg = sum / values.length;
  const verdict: CloudVerdict =
    avg > 80 ? 'overcast' : avg > 50 ? 'cloudy' : avg > 20 ? 'fair' : 'clear';
  return {
    avgPct: Math.round(avg),
    minPct: min,
    maxPct: max,
    clearHours: clear,
    fairHours: fair,
    cloudyHours: cloudy,
    verdict,
  };
}

// Dark-sky score combines (1) cloud cover during the actual dark window
// — not just the day's static cloud % — with (2) inverse moon illumination.
// Clouds weighted more heavily than moon because a half-illuminated moon
// still leaves dark hours available, but 100% overcast kills imaging outright.
export function stargazingScore(
  day: DayBlock,
  darkHoursCloudAvgPct: number | null,
): { score: number; label: string } {
  const clouds = darkHoursCloudAvgPct ?? day.cloudCoverPct ?? 50;
  const moonDark = 100 - day.moonIllumPct;
  const cloudDark = 100 - clouds;
  const raw = cloudDark * 0.7 + moonDark * 0.3;
  const score = Math.max(1, Math.min(5, Math.round(raw / 20)));
  const label =
    score >= 5
      ? 'Excellent'
      : score >= 4
        ? 'Very good'
        : score >= 3
          ? 'Fair'
          : score >= 2
            ? 'Poor'
            : 'Bad';
  return { score, label };
}

// Guidance keyed off the cloud verdict, used by the AstroTonight UI to
// tell the user whether it's worth setting up tonight.
export function cloudGuidance(
  clouds: CloudForecast,
): { tone: string; headline: string; detail: string } {
  if (clouds.avgPct == null) {
    return {
      tone: 'bg-white/10 border-white/10',
      headline: 'Cloud forecast unavailable',
      detail: 'Check the satellite layer for current conditions.',
    };
  }
  const { verdict, avgPct, clearHours, fairHours, cloudyHours } = clouds;
  if (verdict === 'clear') {
    return {
      tone: 'bg-emerald-500/20 border-emerald-400/40',
      headline: 'Clear skies — great imaging conditions',
      detail: `${clearHours}h clear · avg ${avgPct}% cloud during dark hours.`,
    };
  }
  if (verdict === 'fair') {
    return {
      tone: 'bg-sky-500/20 border-sky-400/40',
      headline: 'Mostly clear — imaging viable with thin cloud windows',
      detail: `${clearHours}h clear · ${fairHours}h thin · avg ${avgPct}% cloud.`,
    };
  }
  if (verdict === 'cloudy') {
    return {
      tone: 'bg-amber-500/20 border-amber-400/40',
      headline: 'Broken clouds — marginal, hunt for gaps',
      detail: `${cloudyHours}h cloudy · ${fairHours}h thin · avg ${avgPct}% cloud.`,
    };
  }
  return {
    tone: 'bg-slate-600/30 border-slate-400/40',
    headline: 'Overcast — not recommended for imaging',
    detail: `${cloudyHours}h cloudy · avg ${avgPct}% cloud during dark hours.`,
  };
}

// Guidance strings keyed off dew risk, used by the AstroTonight UI.
export function dewGuidance(risk: DewRisk): { tone: string; headline: string; tips: string[] } {
  if (risk === 'high') {
    return {
      tone: 'bg-rose-500/25 border-rose-400/50',
      headline: 'Heavy dew expected — gear at risk',
      tips: [
        'Run dew heaters on every optic (OTA, finder, guide, eyepieces).',
        'Keep a waterproof cover within reach; use it between targets.',
        'Consider ending the session before peak dew to keep equipment dry.',
      ],
    };
  }
  if (risk === 'moderate') {
    return {
      tone: 'bg-amber-500/20 border-amber-400/40',
      headline: 'Moderate dew likely on optics',
      tips: [
        'Use dew heaters on the objective and corrector (≥1 W/inch aperture).',
        'Check at 1-hour intervals; pack a microfiber for emergencies.',
      ],
    };
  }
  return {
    tone: 'bg-emerald-500/15 border-emerald-400/40',
    headline: 'Dry night expected — equipment should stay clear',
    tips: [],
  };
}
